import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Flex, Heading, Paragraph } from '@rover/kibble/core';
import Overlay from '@rover/kibble/official-patterns/Modal/Overlay';
import useTheme from '@rover/kibble/themes/useTheme';
import { useModalOpenTrackerEffect } from '@rover/kibble/utils/modalOpenTracker';

import { SpacingSystemType } from '../../core/styledSystemPropTypes';
import DialogButtons, { hasDialogButtons } from '../Modal/Content/DialogButtons';
import { showPortalToFocusTrap, usePreventScroll, useReactModalSetup } from '../Modal/Modal.common';

import {
  BottomSheetCommonProps,
  defaultContentPaddingStyles,
  getDismissControls,
} from './BottomSheet.common';
import { DismissButton } from './DismissButton';

export const CLOSE_TIMEOUT = 300;

const DEFAULT_MAX_HEIGHT = '80vh';

const overlayElement = (
  props: React.ComponentPropsWithRef<'div'>,
  contentEl: React.ReactNode
): JSX.Element => (
  <Overlay data-testid="overlay" {...props}>
    {contentEl}
  </Overlay>
);

// Without this, Tab bubbles from a nested sheet to the one beneath, whose focus trap steals focus.
const contentElement = (
  props: React.ComponentPropsWithRef<'div'>,
  children: React.ReactNode
): JSX.Element => (
  // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- props carries role="dialog"
  <div
    {...props}
    onKeyDown={(event) => {
      props.onKeyDown?.(event);
      if (event.key === 'Tab') event.stopPropagation();
    }}
  >
    {children}
  </div>
);

/** Props for web BottomSheet component */
export type Props = BottomSheetCommonProps & {
  /**
   * Styles for react-modal's `content` and `overlay` elements. They apply after the sheet's own
   * position, transform and transition, so they can replace them.
   */
  style?: ReactModal.Styles;
  /**
   * The `id` of the heading in `customHeader`, which names the dialog. `title` needs none. Without
   * either the sheet has no accessible name.
   */
  headerId?: string;
  /**
   * The app's root element, which screen readers can't reach while the sheet is open. Defaults to
   * `#base-content`.
   */
  appElementSelector?: string;
  /** Set `auto` to scroll a body taller than `maxHeight`. By default the body doesn't scroll. */
  overflowY?: 'auto' | 'hidden';
  /**
   * With `isFullHeight`, `horizontal` slides the sheet in from the right instead of up from the
   * bottom. Ignored otherwise. Defaults to `vertical`.
   */
  animationDirection?: 'vertical' | 'horizontal';
  /** Space below the body and footer, outside the scroll area. Defaults to `6x`. */
  paddingBottom?: SpacingSystemType;
};

/** A panel that slides up from the bottom of the screen over a dimmed page. */
const BottomSheet = ({
  isOpen,
  isDismissible = true,
  closeOnDragDown = true,
  isFullHeight = false,
  onRequestClose,
  children,
  title,
  subtitle,
  style,
  headerId,
  customHeader,
  customFooter,
  appElementSelector = '#base-content',
  showCloseIcon: showCloseIconProp,
  hideCloseIcon,
  overflowY,
  contentPaddingStyles = defaultContentPaddingStyles,
  animationDirection: _animationDirection = 'vertical',
  maxHeight = DEFAULT_MAX_HEIGHT,
  paddingBottom = '6x',
  ...buttonProps
}: Props): JSX.Element => {
  const theme = useTheme();
  const { colors } = theme;

  const { staticHeaderId, modalId } = useReactModalSetup(appElementSelector);
  usePreventScroll(isOpen, modalId);
  useModalOpenTrackerEffect(isOpen);

  const [isDragging, setIsDragging] = useState(false);
  const [initialY, setInitialY] = useState(null);
  const [currentY, setCurrentY] = useState(0);
  // A drag that snaps back still ends in a click, which must not close the sheet.
  const hasDraggedRef = useRef(false);

  /** The effective animation direction, as `'horizontal'` would not make sense with a non-full height BottomSheet */
  const appliedAnimationDirection = isFullHeight ? _animationDirection : 'vertical';
  const transformOpen =
    appliedAnimationDirection === 'horizontal' ? 'translateX(0)' : 'translateY(0)';
  const transformClosed =
    appliedAnimationDirection === 'horizontal' ? 'translateX(100%)' : 'translateY(100%)';
  const [transform, setTransform] = useState<CSSProperties['transform']>(
    isOpen ? transformOpen : transformClosed
  );

  // BottomSheet first renders with `isOpen=true`, but we want it to start offscreen so it slides in smoothly,
  // so this offsets the transform state by one frame
  useEffect(() => {
    if (isOpen) {
      setTransform(transformOpen);
    } else {
      setTransform(transformClosed);
    }
  }, [isOpen, transformClosed, transformOpen]);

  const startDrag = (e) => {
    setIsDragging(true);
    hasDraggedRef.current = false;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setInitialY(clientY);
  };

  const onDrag = useCallback(
    (e) => {
      if (!isDragging) return;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const deltaY = clientY - (initialY ?? clientY);
      setCurrentY(Math.max(deltaY, 0)); // Prevent dragging up beyond initial position
      if (Math.abs(deltaY) > 5) hasDraggedRef.current = true;
    },
    [isDragging, initialY]
  );

  const endDrag = useCallback(() => {
    setIsDragging(false);
    if (currentY > 75) {
      // Threshold to close the sheet
      onRequestClose();
    } else {
      setCurrentY(0); // Snap back to the original position
    }
  }, [currentY, onRequestClose]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', endDrag);
      document.addEventListener('touchmove', onDrag);
      document.addEventListener('touchend', endDrag);
    } else {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', endDrag);
    }

    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', endDrag);
    };
  }, [isDragging, endDrag, onDrag]);

  useEffect(() => {
    if (isOpen) {
      setCurrentY(0);
    }
  }, [isOpen]);

  const { showHandle, showCloseIcon } = getDismissControls({
    isFullHeight,
    closeOnDragDown,
    showCloseIcon: showCloseIconProp,
    hideCloseIcon,
    isDismissible,
  });
  const labelId = headerId || staticHeaderId;
  const showButtons = hasDialogButtons(buttonProps);

  let paddingTop: SpacingSystemType = '8x';
  if (showHandle) {
    paddingTop = '0x';
  } else if (isFullHeight) {
    paddingTop = '4x';
  }

  const transition =
    appliedAnimationDirection === 'horizontal'
      ? 'transform 0.4s cubic-bezier(0, 0.78, 0, 0.99), opacity 0.3s'
      : 'transform 0.3s, opacity 0.3s';

  return (
    <ReactModal
      aria={{ labelledby: labelId }}
      testId="bottom-sheet"
      onAfterOpen={showPortalToFocusTrap}
      className="RoverModalContent"
      style={{
        content: {
          width: '100%',
          ...(isFullHeight
            ? {
                height: '100%',
              }
            : {
                position: 'absolute',
                bottom: `-${currentY}px`,
                left: 0,
                right: 0,
              }),
          transform,
          opacity: isOpen ? 1 : 0 /* Start with opacity 0 */,
          transition,
          ...(style?.content && { ...style.content }),
        },
        overlay: {
          padding: 0,
          ...(style?.overlay && { ...style.overlay }),
        },
      }}
      closeTimeoutMS={CLOSE_TIMEOUT}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="RoverModalOverlay"
      overlayElement={overlayElement}
      contentElement={contentElement}
      shouldCloseOnOverlayClick={isDismissible}
      shouldCloseOnEsc={isDismissible || showCloseIcon}
    >
      <Flex
        background="primary"
        flexDirection="column"
        {...(isFullHeight
          ? {
              height: '100%',
              overflowY: 'auto',
            }
          : {
              borderRadius: 'large',
              borderBottomLeftRadius: '0',
              borderBottomRightRadius: '0',
            })}
        minHeight="100px"
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        zIndex={ZIndex.MODAL.toString()}
      >
        {showHandle && (
          <Flex
            alignItems="center"
            width="100%"
            height="48px"
            display="flex"
            justifyContent="center"
            webProps={{
              onMouseMove: onDrag,
              onTouchMove: onDrag,
              onMouseUp: endDrag,
              onTouchEnd: endDrag,
              onMouseDown: startDrag,
              onTouchStart: startDrag,
              tabIndex: 0,
              onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onRequestClose();
                }
              },
            }}
            onClick={() => {
              if (!hasDraggedRef.current) onRequestClose();
            }}
            data-testid="modal-dismiss-button"
            aria-label="Dismiss modal"
            role="button"
            sx={{ cursor: 'pointer' }}
          >
            <Flex
              width="50px"
              height="5px"
              backgroundColor={colors.border.contrast.toString()}
              borderRadius="primary"
            />
          </Flex>
        )}
        <Flex
          flexDirection="column"
          height="100%"
          data-testid="bottom-sheet-content"
          {...(!isFullHeight && { maxHeight })}
        >
          {(showCloseIcon || title || subtitle || customHeader) && (
            <Flex flexDirection="column" mb="4x" {...contentPaddingStyles}>
              <Flex
                alignItems="center"
                justifyContent={title || customHeader ? 'space-between' : 'flex-end'}
              >
                <Box flex="1">
                  {title ? (
                    <Heading id={labelId} size="300" mb="0x">
                      {title}
                    </Heading>
                  ) : (
                    customHeader
                  )}
                </Box>
                {showCloseIcon && <DismissButton onRequestClose={onRequestClose} />}
              </Flex>
              {subtitle && (
                <Paragraph size="200" mt="2x" mb="0x">
                  {subtitle}
                </Paragraph>
              )}
            </Flex>
          )}
          <Box
            {...contentPaddingStyles}
            data-testid="bottom-sheet-body"
            sx={{ overflowY }}
            height={isFullHeight ? '100%' : 'auto'}
          >
            {children}
          </Box>
          {showButtons && (
            <Box {...contentPaddingStyles}>
              <DialogButtons {...buttonProps} />
            </Box>
          )}
          {customFooter && (
            <Box {...contentPaddingStyles} {...(showButtons && { mt: '4x' })}>
              {customFooter}
            </Box>
          )}
        </Flex>
      </Flex>
    </ReactModal>
  );
};

export default BottomSheet;
